import { Component, OnDestroy, OnInit, computed, inject } from "@angular/core";
import { compareByLocalizedNameNoneFirst, WkTranslatePipe } from "@shared/index";
import { Subscription } from "rxjs";
import {
    CardViewService,
    ColumnDataLayoutService,
    DashboardsService,
    FiltersService,
    LocalStorageService,
    IFirmGroupsService,
    IFirmRolesService,
    UtilsService,
} from "../../shared";
import { HelpersService } from "../../shared/helpers-service/helpers.service";
import { MappingOfStatusesService } from "../../shared/mapping-of-statuses-service/mapping-of-statuses.service";
import { IStepsModel } from "../../views-interfaces/steps-model.interface";
import { ITranslateObject, SupportedLanguage } from "../../views-interfaces";
import { isJobsBilling } from "@app/views-interfaces/ApplicationType.enum";
import { LangChangeEvent, TranslateService } from "@ngx-translate/core";
import { Option } from "@app/filter-menu/filter-types";
import { ColumnFilterSelection } from "../../views-interfaces/column-filter-selection.interface";
import { VIRTUAL_SCROLL_THRESHOLD } from "@app/assignees-columns-wrapper/assignees-column-scroll-strategy";

const MANAGER_ROLE_ID = "1";
const PARTNER_ROLE_ID = "2";

@Component({
    selector: "app-assignees-view",
    templateUrl: "./assignees-view.component.html",
    styleUrl: "./assignees-view.component.scss",
    standalone: false,
    host: { class: "wk-av" },
    providers: [WkTranslatePipe],
})
export class AssigneesViewComponent implements OnInit, OnDestroy {
    private readonly cardViewService = inject(CardViewService);
    private readonly columnDataLayoutService = inject(ColumnDataLayoutService); // TODO This is injected to perform side-effects, we should refactor and remove
    private readonly dashboardsService = inject(DashboardsService);
    private readonly filtersService = inject(FiltersService);
    private readonly helpersService = inject(HelpersService);
    private readonly mappingOfStatusesService = inject(MappingOfStatusesService);
    private readonly iFirmRolesService = inject(IFirmRolesService);
    private readonly iFirmGroupsService = inject(IFirmGroupsService);
    private readonly translateService = inject(TranslateService);

    steps: IStepsModel[] = [];
    selectedStepId = "";
    statuses = [];
    stepRelatedStatuses = [];
    selectedStatus;
    cardLoadedSubscription: Subscription;
    dashboardChangeSubscription: Subscription;
    firstColumnVisible = true;
    readonly isJobsBillingDashboard = computed(() => {
        const appType = this.dashboardsService.currentDashboard()?.ApplicationType;
        return appType !== undefined ? isJobsBilling(appType) : false;
    });

    rolesOptions: Option[] = [];
    selectedRoles: Option[] = [];
    rolesSearchText = "";
    filteredRolesOptions: Option[] = [];

    usersOptions: Option[] = [];
    selectedUsers: Option[] = [];
    usersSearchText = "";
    filteredUsersOptions: Option[] = [];
    isUsersDisabled = true;

    userGroup1Options: Option[] = [];
    selectedUserGroup1: Option[] = [];
    userGroup1SearchText = "";
    filteredUserGroup1Options: Option[] = [];
    isGroup1Visible = false;
    group1Label = "";

    userGroup2Options: Option[] = [];
    selectedUserGroup2: Option[] = [];
    userGroup2SearchText = "";
    filteredUserGroup2Options: Option[] = [];
    isGroup2Visible = false;
    group2Label = "";

    protected currentLanguage: SupportedLanguage = "en-CA";
    isGroupDisabled: boolean = false;
    columnFilterSelection: ColumnFilterSelection = { roles: [], users: [], userGroup1: [], userGroup2: [] };
    displayedColumnsCount = 0;
    isInitialized = false;

    get isDragDropDisabled(): boolean {
        return this.displayedColumnsCount > VIRTUAL_SCROLL_THRESHOLD;
    }

    constructor() {
        this.currentLanguage = (
            this.translateService.getCurrentLang() ? this.translateService.getCurrentLang() : this.translateService.getFallbackLang()
        ) as SupportedLanguage;
        this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
            this.currentLanguage = event.lang as SupportedLanguage;
        });

        this.cardLoadedSubscription = this.columnDataLayoutService.loadCardsFromCacheEvent$.subscribe(async (data) => {
            this.isInitialized = false;
            await this.init();
            this.isInitialized = true;
        });

        this.dashboardChangeSubscription = this.dashboardsService.dashboardChangeEvent$.subscribe(() => {
            this.isInitialized = false;
        });
    }

    ngOnInit() {
        if (this.columnDataLayoutService.cardsAreLoaded) {
            // Re-entry: cards already loaded, subscription won't re-fire
            this.isInitialized = false;
            this.init().then(() => {
                this.isInitialized = true;
            });
        }
    }
    getSelectedStep(): any {
        return this.steps.find((element) => !element.Disabled && element.State === this.cardViewService.currentViewType);
    }

    getLocalStorageKey(keyName): string {
        return (
            this.helpersService.getCurrentUser().sub + this.dashboardsService.getCurrentDashboardId() + this.cardViewService.currentViewType + keyName
        );
    }

    getColumnFilterLocalStorageKey(filterName: string): string {
        return (
            this.helpersService.getCurrentUser().sub +
            "_" +
            this.dashboardsService.getCurrentDashboardId() +
            "_" +
            this.cardViewService.currentViewType +
            "_" +
            this.selectedStepId +
            "_" +
            this.selectedStatus?.TaxReturnStatus?.Id +
            "_columnFilter_" +
            filterName
        );
    }

    ngOnDestroy() {
        this.cardLoadedSubscription.unsubscribe();
        this.dashboardChangeSubscription.unsubscribe();
    }

    async init() {
        await this.filtersService.refreshFiltersDependencies();
        await this.helpersService.mergeSteps();
        this.getAllSteps();
        const cachedStepId = localStorage.getItem(this.getLocalStorageKey("StepID"));
        if (cachedStepId == null || this.steps.findIndex((step) => step.Id == cachedStepId) < 0) {
            this.selectedStepId = this.getSelectedStep()?.Id;
            LocalStorageService.setItem(this.getLocalStorageKey("StepID"), this.selectedStepId);
        } else {
            this.selectedStepId = cachedStepId;
        }
        this.getAllStatuses();
        this.loadFirstColumnVisibleFromLocalStorage();
        await this.initColumnFilters();
    }

    toggleFirstColumnWidth(): void {
        this.firstColumnVisible = !this.firstColumnVisible;
        this.saveFirstColumnVisibleToLocalStorage();
    }
    getAllSteps() {
        // Get steps from service then sort them
        this.steps = UtilsService.recursiveDeepCopy(this.mappingOfStatusesService.Steps).filter(
            (step) => step.State === this.cardViewService.currentViewType,
        );

        if (this.steps && this.steps.length > 0) {
            this.steps.sort((a, b) => {
                if (a.Order > b.Order) {
                    return 1;
                }
                if (a.Order < b.Order) {
                    return -1;
                }
                return 0;
            });
        }
    }

    getAllStatuses() {
        // Get statuses from service then filter them to match step
        this.statuses = this.mappingOfStatusesService.getAllReturnStatuses();
        this.selectedStatus = this.getFirstSelectedStatus();
    }

    getFirstSelectedStatus() {
        // Find related statuses to step
        this.stepRelatedStatuses = this.findRelatedStatuses(this.selectedStepId);
        // Select the first available status
        const cachedStepStatus = localStorage.getItem(this.getLocalStorageKey("StepReturnStatus") + this.selectedStepId);
        if (cachedStepStatus) {
            if (this.stepRelatedStatuses.findIndex((status) => status.TaxReturnStatus.Id == cachedStepStatus && !status.disabled) < 0) {
                if (this.stepRelatedStatuses && this.stepRelatedStatuses.length > 0) {
                    for (const status of this.stepRelatedStatuses) {
                        if (!status.disabled) {
                            return status;
                        }
                    }
                }
            } else {
                if (this.stepRelatedStatuses && this.stepRelatedStatuses.length > 0) {
                    return this.stepRelatedStatuses.find((status) => status.TaxReturnStatus.Id == cachedStepStatus);
                }
            }
        } else {
            if (this.stepRelatedStatuses && this.stepRelatedStatuses.length > 0) {
                for (const status of this.stepRelatedStatuses) {
                    if (!status.disabled) {
                        return status;
                    }
                }
            }
        }
    }

    findRelatedStatuses(stepId): any[] {
        const relatedStatusesArray = [];
        const relatedStatusesIdArray = [];
        const currentStep = this.steps.find((step) => step.Id === stepId);
        if (currentStep !== null && currentStep !== undefined) {
            currentStep.Statuses.forEach((status) => relatedStatusesIdArray.push(status.TaxReturnStatusId));
            for (const status of this.statuses) {
                if (status.TaxReturnStatus && relatedStatusesIdArray.includes(status.TaxReturnStatus.Id)) {
                    relatedStatusesArray.push(status);
                }
            }
        }
        return relatedStatusesArray;
    }

    updateStep(stepId: string): void {
        if (stepId) {
            this.selectedStepId = stepId;
            LocalStorageService.setItem(this.getLocalStorageKey("StepID"), this.selectedStepId);
            this.selectedStatus = this.getFirstSelectedStatus();
            LocalStorageService.setItem(this.getLocalStorageKey("StepReturnStatus") + this.selectedStepId, this.selectedStatus?.TaxReturnStatus?.Id);
            this.cardViewService.assigneesStatusChangeSubject.next({
                stepId: this.selectedStepId,
                statusId: this.selectedStatus?.TaxReturnStatus?.Id,
            });
            this.loadColumnFilterSelections();
        }
    }

    updateStatus(statusId: string): void {
        if (statusId) {
            this.selectedStatus = this.stepRelatedStatuses.find((x) => x.TaxReturnStatus.Id === statusId);
            LocalStorageService.setItem(this.getLocalStorageKey("StepReturnStatus") + this.selectedStepId, this.selectedStatus?.TaxReturnStatus?.Id);
            this.cardViewService.assigneesStatusChangeSubject.next({
                stepId: this.selectedStepId,
                statusId: this.selectedStatus?.TaxReturnStatus?.Id,
            });
            this.loadColumnFilterSelections();
        }
    }

    saveFirstColumnVisibleToLocalStorage() {
        LocalStorageService.setItem(
            "firstColumnVisible" +
                "_" +
                this.dashboardsService.getCurrentDashboardId() +
                "_" +
                this.cardViewService.currentViewType +
                "_" +
                this.helpersService.getCurrentUser().sub,
            this.firstColumnVisible,
        );
    }

    loadFirstColumnVisibleFromLocalStorage() {
        const value = localStorage.getItem(
            "firstColumnVisible" +
                "_" +
                this.dashboardsService.getCurrentDashboardId() +
                "_" +
                this.cardViewService.currentViewType +
                "_" +
                this.helpersService.getCurrentUser().sub,
        );
        if (value) {
            this.firstColumnVisible = value === "true";
        }
    }

    async initColumnFilters() {
        if (this.isJobsBillingDashboard()) {
            await this.buildRolesOptions();
        }
        await this.iFirmGroupsService.getiFirmGroups();
        this.checkGroupVisibility();
        await this.buildUserGroupOptions();
        this.loadColumnFilterSelections();
        this.rebuildUsersOptions();
    }

    async buildRolesOptions() {
        await this.iFirmRolesService.getiFirmRoles();
        const roles = this.iFirmRolesService.iFirmRoles;
        const priorityOrder = [PARTNER_ROLE_ID, MANAGER_ROLE_ID];

        this.rolesOptions = roles
            .slice()
            .sort((a, b) => {
                const aPriority = priorityOrder.indexOf(a.Id);
                const bPriority = priorityOrder.indexOf(b.Id);

                if (aPriority !== -1 && bPriority !== -1) return aPriority - bPriority;
                if (aPriority !== -1) return -1;
                if (bPriority !== -1) return 1;

                if (parseInt(a.Id, 10) < parseInt(b.Id, 10)) return -1;
                if (parseInt(a.Id, 10) > parseInt(b.Id, 10)) return 1;
                return 0;
            })
            .map((role) => ({
                text: role.Description as string | ITranslateObject,
                value: role.Id,
            }));

        this.filteredRolesOptions = [...this.rolesOptions];
    }

    rebuildUsersOptions() {
        let roleIds: string[];
        const eligibleUsersMap = new Map<string, any>();
        this.isUsersDisabled = false;

        if (this.isJobsBillingDashboard()) {
            roleIds = (this.selectedRoles ?? []).map((r) => r.value as string);
        } else {
            const statusRoleId = this.selectedStatus?.Role?.Id;
            roleIds = statusRoleId != null ? [statusRoleId.toString()] : [];
        }

        if (roleIds.length === 0) {
            this.usersOptions = [];
            this.filteredUsersOptions = [];
            this.selectedUsers = [];
            this.isUsersDisabled = true;
            return;
        }

        const rolesWithUsers = this.iFirmRolesService.rolesWithUsers();

        for (const roleId of roleIds) {
            const roleData = rolesWithUsers.get(roleId);
            if (roleData?.Users) {
                for (const [userId, user] of roleData.Users.entries()) {
                    if (user.Active && user.LicenseEnabled && !eligibleUsersMap.has(userId)) {
                        eligibleUsersMap.set(userId, user);
                    }
                }
            }
        }

        const eligibleUsers = Array.from(eligibleUsersMap.values());
        eligibleUsers.sort((a, b) => {
            const nameA = `${a.FirstName} ${a.LastName}`.toLowerCase();
            const nameB = `${b.FirstName} ${b.LastName}`.toLowerCase();
            return nameA.localeCompare(nameB);
        });

        this.usersOptions = eligibleUsers.map((user) => ({
            text: {
                "en-CA": `${user.FirstName} ${user.LastName}`,
                "fr-CA": `${user.FirstName} ${user.LastName}`,
            } as ITranslateObject,
            value: user.Id,
        }));

        this.filteredUsersOptions = [...this.usersOptions];

        this.selectedUsers = this.selectedUsers.filter((selected) => this.usersOptions.some((option) => option.value === selected.value));
    }

    checkGroupVisibility() {
        this.isGroup1Visible = this.iFirmGroupsService.getGroup1Enabled() && this.iFirmGroupsService.getGroup1UsersEnabled();
        this.isGroup2Visible = this.iFirmGroupsService.getGroup2Enabled() && this.iFirmGroupsService.getGroup2UsersEnabled();
        this.refreshFilterLabels();
    }

    refreshFilterLabels() {
        this.group1Label =
            this.currentLanguage === "fr-CA"
                ? this.iFirmGroupsService.getGroup1Caption("fr-CA") + " de l'utilisateur"
                : "User " + this.iFirmGroupsService.getGroup1Caption("en-CA");
        this.group2Label =
            this.currentLanguage === "fr-CA"
                ? this.iFirmGroupsService.getGroup2Caption("fr-CA") + " de l'utilisateur "
                : "User " + this.iFirmGroupsService.getGroup2Caption("en-CA");
    }

    async buildUserGroupOptions() {
        await this.iFirmGroupsService.getiFirmGroups();

        if (this.isJobsBillingDashboard()) {
            this.isGroupDisabled = false;
        } else {
            const noRoleAssociated = this.selectedStatus?.Role?.Id == null;
            this.isGroupDisabled = noRoleAssociated;
        }

        if (this.isGroup1Visible) {
            const group1Options = this.iFirmGroupsService.getGroup1Options(false) ?? [];
            this.userGroup1Options = group1Options
                .sort((a, b) => {
                    return compareByLocalizedNameNoneFirst(a, b, this.currentLanguage);
                })
                .map((option) => ({
                    text: {
                        "en-CA": option["en-CA"],
                        "fr-CA": option["fr-CA"],
                    } as ITranslateObject,
                    value: option.Id.toString(),
                }));

            this.filteredUserGroup1Options = [...this.userGroup1Options];
        }

        if (this.isGroup2Visible) {
            const group2Options = this.iFirmGroupsService.getGroup2Options(false) ?? [];
            this.userGroup2Options = group2Options
                .sort((a, b) => {
                    return compareByLocalizedNameNoneFirst(a, b, this.currentLanguage);
                })
                .map((option) => ({
                    text: {
                        "en-CA": option["en-CA"],
                        "fr-CA": option["fr-CA"],
                    } as ITranslateObject,
                    value: option.Id.toString(),
                }));
            this.filteredUserGroup2Options = [...this.userGroup2Options];
        }

        this.refreshFilterLabels();
    }

    onRolesSelectionChange(selected: Option[]) {
        this.selectedRoles = selected;
        this.rebuildUsersOptions();
        this.saveColumnFilterSelection("roles", this.selectedRoles);
        this.saveColumnFilterSelection("users", this.selectedUsers);
        this.applyColumnFilters();
    }

    onUsersSelectionChange(selected: Option[]) {
        this.selectedUsers = selected;
        this.saveColumnFilterSelection("users", this.selectedUsers);
        this.applyColumnFilters();
    }

    onUserGroup1SelectionChange(selected: Option[]) {
        this.selectedUserGroup1 = selected;
        this.saveColumnFilterSelection("userGroup1", this.selectedUserGroup1);
        this.applyColumnFilters();
    }

    onUserGroup2SelectionChange(selected: Option[]) {
        this.selectedUserGroup2 = selected;
        this.saveColumnFilterSelection("userGroup2", this.selectedUserGroup2);
        this.applyColumnFilters();
    }

    onRolesSearch(searchText: string) {
        this.rolesSearchText = searchText;
        const lower = searchText.toLowerCase();
        this.filteredRolesOptions = this.rolesOptions.filter((option) => {
            const text = this.getOptionText(option);
            return text.toLowerCase().includes(lower);
        });
    }

    onUsersSearch(searchText: string) {
        this.usersSearchText = searchText;
        const lower = searchText.toLowerCase();
        this.filteredUsersOptions = this.usersOptions.filter((option) => {
            const text = this.getOptionText(option);
            return text.toLowerCase().includes(lower);
        });
    }

    onUserGroup1Search(searchText: string) {
        this.userGroup1SearchText = searchText;
        const lower = searchText.toLowerCase();
        this.filteredUserGroup1Options = this.userGroup1Options.filter((option) => {
            const text = this.getOptionText(option);
            return text.toLowerCase().includes(lower);
        });
    }

    onUserGroup2Search(searchText: string) {
        this.userGroup2SearchText = searchText;
        const lower = searchText.toLowerCase();
        this.filteredUserGroup2Options = this.userGroup2Options.filter((option) => {
            const text = this.getOptionText(option);
            return text.toLowerCase().includes(lower);
        });
    }

    async applyColumnFilters() {
        this.columnFilterSelection = {
            roles: [...this.selectedRoles],
            users: [...this.selectedUsers],
            userGroup1: [...this.selectedUserGroup1],
            userGroup2: [...this.selectedUserGroup2],
        };
    }

    saveColumnFilterSelection(filterName: string, selection: Option[]) {
        const key = this.getColumnFilterLocalStorageKey(filterName);
        const serialized = JSON.stringify(selection.map((s) => ({ text: s.text, value: s.value })));
        LocalStorageService.setItem(key, serialized);
    }

    loadColumnFilterSelections() {
        this.selectedRoles = this.loadFilterFromStorage("roles", this.rolesOptions);
        this.rebuildUsersOptions();
        this.selectedUsers = this.loadFilterFromStorage("users", this.usersOptions);
        this.selectedUserGroup1 = this.loadFilterFromStorage("userGroup1", this.userGroup1Options);
        this.selectedUserGroup2 = this.loadFilterFromStorage("userGroup2", this.userGroup2Options);
        this.applyColumnFilters();
    }

    private loadFilterFromStorage(filterName: string, availableOptions: Option[]): Option[] {
        const key = this.getColumnFilterLocalStorageKey(filterName);
        const stored = localStorage.getItem(key);
        if (!stored) return [];
        try {
            const parsed: Option[] = JSON.parse(stored);
            return parsed.filter((stored) => availableOptions.some((opt) => opt.value === stored.value));
        } catch {
            return [];
        }
    }

    getOptionText(option: Option): string {
        if (typeof option.text === "string") return option.text;
        return (option.text as ITranslateObject)?.[this.currentLanguage] || (option.text as ITranslateObject)?.["en-CA"] || "";
    }

    onDisplayedColumnsCountChange(count: number): void {
        this.displayedColumnsCount = count;
    }
}
